import { Fragment, useMemo, useState, type CSSProperties } from 'react';

import './app.css';
import {
    followups,
    ownerLabels,
    phaseCopy,
    phases,
    processSteps,
    segments,
    type Followup,
    type Owner,
    type PhaseName,
    type Segment,
} from './timelineData';

const DAY = 24 * 60 * 60 * 1000;

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
});

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
});

type Selection =
    { type: 'segment'; item: Segment } | { type: 'followup'; item: Followup };

type CustomStyle = CSSProperties & {
    '--owner-color'?: string;
    '--owner-soft'?: string;
    '--x'?: string;
    '--lane'?: number;
};

function toDate(value: string) {
    return new Date(value);
}

function elapsed(start: string, end: string) {
    return toDate(end).getTime() - toDate(start).getTime();
}

function sum(items: Segment[]) {
    return items.reduce(
        (total, item) => total + elapsed(item.start, item.end),
        0,
    );
}

function duration(milliseconds: number, precise = false) {
    const totalMinutes = Math.round(milliseconds / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (!precise && milliseconds >= DAY * 2) {
        return `${(milliseconds / DAY).toFixed(1)} days`;
    }
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function ownerClass(owner: Owner) {
    return `owner-${owner.toLowerCase()}`;
}

function ownerStyle(owner: Owner): CustomStyle {
    return {
        '--owner-color': `var(--${owner.toLowerCase()})`,
        '--owner-soft': `var(--${owner.toLowerCase()}-soft)`,
    };
}

function phaseSegments(phase: PhaseName) {
    return segments.filter((item) => item.phase === phase);
}

function DetailPanel({ selection }: { selection: Selection }) {
    if (selection.type === 'followup') {
        const item = selection.item;
        return (
            <div
                className="selected-detail owner-scott"
                style={ownerStyle('Scott')}
                aria-live="polite"
            >
                <p className="detail-title">
                    Follow-up {item.number} · {item.title}
                </p>
                <p className="detail-meta">
                    {dateTimeFormatter.format(toDate(item.at))} · {item.kind}
                </p>
                <p>
                    {item.evidence} This marker is a communication prompt, not
                    an ownership handoff or time allocation.
                </p>
            </div>
        );
    }

    const item = selection.item;
    return (
        <div
            className={`selected-detail ${ownerClass(item.owner)}`}
            style={ownerStyle(item.owner)}
            aria-live="polite"
        >
            <p className="detail-title">
                {ownerLabels[item.owner]} · {item.title}
            </p>
            <p className="detail-meta">
                {dateTimeFormatter.format(toDate(item.start))} →{' '}
                {dateTimeFormatter.format(toDate(item.end))} ·{' '}
                {duration(elapsed(item.start, item.end), true)}
                {item.estimated ? ' · estimated boundary' : ''}
            </p>
            <p>{item.evidence}</p>
        </div>
    );
}

function ProcessPath() {
    return (
        <section aria-labelledby="path-title">
            <div className="section-heading">
                <h2 id="path-title">The actual H-1B path</h2>
                <p className="section-note">
                    The TN questionnaire is excluded because the record never
                    identifies it as a prerequisite for BAL’s legal
                    recommendation or HR authorization.
                </p>
            </div>
            <ol className="process-flow">
                {processSteps.map((step) => (
                    <li
                        className={`process-step process-${step.status}`}
                        key={step.number}
                    >
                        <p className="step-status">
                            {step.number} · {step.status}
                        </p>
                        <strong>{step.title}</strong>
                        <span>{step.detail}</span>
                    </li>
                ))}
            </ol>
        </section>
    );
}

function HandoffChart() {
    const [selection, setSelection] = useState<Selection>({
        type: 'segment',
        item: segments[segments.length - 1],
    });

    return (
        <section aria-labelledby="handoff-title">
            <div className="section-heading">
                <h2 id="handoff-title">Every handoff on the critical path</h2>
                <p className="section-note">
                    Width represents elapsed calendar time. Numbered blue
                    markers are Scott’s status follow-ups and urgency prompts.
                    Select a colored section or marker for its dated evidence.
                </p>
            </div>

            <div className="handoff-chart">
                {phases.map((phase) => {
                    const items = phaseSegments(phase);
                    const phaseStart = toDate(items[0].start).getTime();
                    const phaseEnd = toDate(
                        items[items.length - 1].end,
                    ).getTime();
                    const total = phaseEnd - phaseStart;
                    const ownerShares = (Object.keys(ownerLabels) as Owner[])
                        .map((owner) => ({
                            owner,
                            milliseconds: sum(
                                items.filter((item) => item.owner === owner),
                            ),
                        }))
                        .filter((entry) => entry.milliseconds > 0)
                        .sort(
                            (left, right) =>
                                right.milliseconds - left.milliseconds,
                        );
                    const ticks = Array.from({ length: 5 }, (_, index) =>
                        shortDateFormatter.format(
                            new Date(phaseStart + total * (index / 4)),
                        ),
                    );

                    return (
                        <article className="ribbon-group" key={phase}>
                            <div className="ribbon-label">
                                <h3>{phaseCopy[phase].title}</h3>
                                <div className="ribbon-summary">
                                    <span>{duration(total)}</span>
                                    <span className="owner-shares">
                                        {ownerShares
                                            .map(
                                                (entry) =>
                                                    `${ownerLabels[entry.owner]} ${((entry.milliseconds / total) * 100).toFixed(1)}%`,
                                            )
                                            .join(' · ')}
                                    </span>
                                </div>
                            </div>
                            <div className="axis" aria-hidden="true">
                                {ticks.map((tick, index) => (
                                    <span key={`${tick}-${index}`}>{tick}</span>
                                ))}
                            </div>
                            <div
                                className="handoff-ribbon"
                                role="group"
                                aria-label={`${phase} handoff timeline`}
                            >
                                {items.map((item) => {
                                    const isSelected =
                                        selection.type === 'segment' &&
                                        selection.item.id === item.id;
                                    return (
                                        <span
                                            className={`ribbon-segment ${ownerClass(item.owner)}${isSelected ? ' is-selected' : ''}`}
                                            style={{
                                                ...ownerStyle(item.owner),
                                                flexGrow: elapsed(
                                                    item.start,
                                                    item.end,
                                                ),
                                                flexBasis: 0,
                                            }}
                                            aria-hidden="true"
                                            key={item.id}
                                        />
                                    );
                                })}
                                <div className="ribbon-hit-layer">
                                    {items.map((item) => {
                                        const isSelected =
                                            selection.type === 'segment' &&
                                            selection.item.id === item.id;
                                        return (
                                            <button
                                                type="button"
                                                className="ribbon-hit-target"
                                                style={{
                                                    flexGrow: elapsed(
                                                        item.start,
                                                        item.end,
                                                    ),
                                                    flexBasis: 0,
                                                }}
                                                aria-label={`${ownerLabels[item.owner]}: ${item.title}, ${duration(elapsed(item.start, item.end), true)}`}
                                                aria-pressed={isSelected}
                                                onClick={() =>
                                                    setSelection({
                                                        type: 'segment',
                                                        item,
                                                    })
                                                }
                                                key={item.id}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <div
                                className="annotation-track"
                                role="group"
                                aria-label={`${phase} phase follow-ups from Scott`}
                            >
                                {followups
                                    .filter((item) => item.phase === phase)
                                    .map((item) => {
                                        const percent =
                                            ((toDate(item.at).getTime() -
                                                phaseStart) /
                                                total) *
                                            100;
                                        const isSelected =
                                            selection.type === 'followup' &&
                                            selection.item.number ===
                                                item.number;
                                        const markerStyle: CustomStyle = {
                                            '--x': `${percent}%`,
                                            '--lane': item.lane,
                                        };
                                        return (
                                            <Fragment key={item.number}>
                                                <span
                                                    className="followup-stem"
                                                    style={markerStyle}
                                                    aria-hidden="true"
                                                />
                                                <button
                                                    type="button"
                                                    className="followup-marker"
                                                    style={markerStyle}
                                                    aria-label={`Follow-up ${item.number}: ${item.title}, ${dateTimeFormatter.format(toDate(item.at))}`}
                                                    aria-pressed={isSelected}
                                                    onClick={() =>
                                                        setSelection({
                                                            type: 'followup',
                                                            item,
                                                        })
                                                    }
                                                >
                                                    {item.number}
                                                </button>
                                            </Fragment>
                                        );
                                    })}
                            </div>
                            <label className="mobile-handoff-select">
                                <span>Inspect a handoff</span>
                                <select
                                    value={
                                        selection.type === 'segment' &&
                                        selection.item.phase === phase
                                            ? selection.item.id
                                            : ''
                                    }
                                    onChange={(event) => {
                                        const item = items.find(
                                            (candidate) =>
                                                candidate.id ===
                                                event.target.value,
                                        );
                                        if (item) {
                                            setSelection({
                                                type: 'segment',
                                                item,
                                            });
                                        }
                                    }}
                                >
                                    <option value="">Choose a section</option>
                                    {items.map((item) => (
                                        <option value={item.id} key={item.id}>
                                            {ownerLabels[item.owner]} ·{' '}
                                            {item.title}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {selection.item.phase === phase ? (
                                <DetailPanel selection={selection} />
                            ) : null}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

function AuditTrail() {
    return (
        <section aria-label="Evidence audit trails" className="audit-section">
            <details>
                <summary>Audit trail · 8 attributed intervals</summary>
                <div className="audit-list">
                    {segments.map((item) => (
                        <div className="audit-row" key={item.id}>
                            <time dateTime={item.start}>
                                {shortDateFormatter.format(toDate(item.start))}
                            </time>
                            <span
                                className={`owner-badge ${ownerClass(item.owner)}`}
                                style={ownerStyle(item.owner)}
                            >
                                {ownerLabels[item.owner]}
                            </span>
                            <div className="audit-event">
                                <strong>{item.title}</strong>
                                <span>{item.evidence}</span>
                            </div>
                            <span className="audit-duration">
                                {duration(elapsed(item.start, item.end), true)}
                                {item.estimated ? ' *' : ''}
                            </span>
                        </div>
                    ))}
                </div>
            </details>
            <details>
                <summary>
                    Follow-up log · 9 Scott prompts + 1 Datadog prompt
                </summary>
                <div className="audit-list">
                    {followups.map((item) => (
                        <div className="audit-row" key={item.number}>
                            <time dateTime={item.at}>
                                {dateTimeFormatter.format(toDate(item.at))}
                            </time>
                            <span
                                className="owner-badge owner-scott"
                                style={ownerStyle('Scott')}
                            >
                                Scott
                            </span>
                            <div className="audit-event">
                                <strong>
                                    Follow-up {item.number} · {item.title}
                                </strong>
                                <span>{item.evidence}</span>
                            </div>
                            <span className="audit-duration">{item.kind}</span>
                        </div>
                    ))}
                    <div className="audit-row">
                        <time dateTime="2026-07-06T09:13:00-04:00">
                            Jul 6, 9:13 AM
                        </time>
                        <span
                            className="owner-badge owner-hr"
                            style={ownerStyle('HR')}
                        >
                            Datadog HR
                        </span>
                        <div className="audit-event">
                            <strong>
                                Nathalie independently asked whether the issue
                                had been addressed
                            </strong>
                            <span>
                                This separate Datadog prompt preceded Scott’s
                                July 6 status summary by about an hour.
                            </span>
                        </div>
                        <span className="audit-duration">Datadog prompt</span>
                    </div>
                </div>
            </details>
        </section>
    );
}

function App() {
    const metrics = useMemo(() => {
        const decision = phaseSegments('Decision');
        const allTime = sum(segments);
        const scottTime = sum(
            segments.filter((item) => item.owner === 'Scott'),
        );
        return {
            decisionDays: `${(sum(decision) / DAY).toFixed(1)}d`,
            scottTime: duration(scottTime, true),
            outsideScott: `${(((allTime - scottTime) / allTime) * 100).toFixed(1)}%`,
        };
    }, []);

    return (
        <main className="page">
            <div className="topline">
                <p className="eyebrow">
                    Process timeline · Based on documented correspondence
                </p>
                <button
                    className="print-button"
                    type="button"
                    onClick={() => window.print()}
                >
                    Print / save PDF
                </button>
            </div>

            <header className="hero">
                <div>
                    <p className="eyebrow">TN → H-1B process overview</p>
                    <h1>H-1B process timeline and remaining steps</h1>
                    <p className="lede">
                        From the initial strategy question through HR approval,
                        73.7 calendar days elapsed. This brief documents the
                        sequence, handoffs, completed inputs, and remaining
                        filing steps.
                    </p>
                </div>
                <aside className="status" aria-label="Current case status">
                    <p className="status-label">Most recently documented</p>
                    <strong>LCA in attorney review</strong>
                    <p>
                        LCA submission and H-1B filing were not confirmed in the
                        correspondence reviewed.
                    </p>
                </aside>
            </header>

            <section className="stats" aria-label="Key elapsed-time measures">
                <article className="stat">
                    <p className="stat-label">Strategy decision</p>
                    <p className="stat-value">{metrics.decisionDays}</p>
                    <p>Apr 24 question → Jul 7 HR approval communicated</p>
                </article>
                <article className="stat">
                    <p className="stat-label">Scott on H-1B-specific inputs</p>
                    <p className="stat-value">{metrics.scottTime}</p>
                    <p>Questionnaire plus newly requested documents</p>
                </article>
                <article className="stat">
                    <p className="stat-label">
                        Total critical path outside Scott
                    </p>
                    <p className="stat-value">{metrics.outsideScott}</p>
                    <p>Apr 24 strategy question → Jul 15 LCA-review reply</p>
                </article>
            </section>

            <ProcessPath />
            <HandoffChart />

            <section className="argument" aria-labelledby="argument-title">
                <div>
                    <p className="eyebrow">
                        The clearest accountability window
                    </p>
                    <p className="argument-number">{metrics.outsideScott}</p>
                    <p className="argument-caption">
                        of the H-1B critical path from the first strategy
                        question through the latest LCA-review reply was outside
                        Scott’s control.
                    </p>
                </div>
                <div>
                    <h2 id="argument-title">What the record supports saying</h2>
                    <ul className="argument-list">
                        <li>
                            The TN questionnaire was a fallback-path task. BAL
                            never said it blocked the legal recommendation to
                            use H-1B or the request for HR authorization.
                        </li>
                        <li>
                            The record contains six explicit status or next-step
                            follow-ups from Scott and three additional urgency
                            prompts. Nathalie separately followed up on July 6.
                        </li>
                        <li>
                            BAL held 56.1 days before stating that it would
                            approach HR about H-1B, then another 5.7 days to the
                            documented HR meeting.
                        </li>
                        <li>
                            Scott completed every H-1B-specific request in about
                            five hours total: 3.1 hours for the questionnaire
                            and 1.9 hours for documents.
                        </li>
                        <li>
                            As of BAL’s July 15 reply, the LCA remained in
                            attorney review. Neither LCA submission nor H-1B
                            filing had been confirmed.
                        </li>
                    </ul>
                </div>
            </section>

            <AuditTrail />

            <section
                className="notes"
                aria-label="Methodology and source notes"
            >
                <div>
                    <h3>Attribution rule</h3>
                    <p>
                        “Held the clock” means the party with the next
                        documented H-1B decision, approval, input, or filing
                        action—not hours of labor. Intervals are mutually
                        exclusive.
                    </p>
                </div>
                <div>
                    <h3>Boundary assumptions</h3>
                    <p>
                        The TN questionnaire is excluded as a parallel fallback
                        task. The June 25 HR-meeting boundary is estimated at
                        noon because the emails establish the date, not the
                        time.
                    </p>
                </div>
                <div>
                    <h3>Evidence base</h3>
                    <p>
                        BAL/Datadog Gmail threads, the communication analysis,
                        and authenticated Cobalt records for H-1B matter
                        21118.41.10 and LCA matter 21118.41.11.
                    </p>
                </div>
            </section>

            <footer>
                This is an evidence-based communication timeline, not legal
                advice or a determination of professional misconduct.
            </footer>
        </main>
    );
}

export default App;
